/**
 * PropertyFilter query → ListTaggedPipelines `sfdcPipelinePropertyFilter` input.
 *
 * The Pipeline View filters opps by SFDC pipeline properties (Stage / Account /
 * Oppty Name / Opp ID / Opp Owner) through a Cloudscape PropertyFilter with
 * one-level AND/OR grouping (`enableTokenGroups`). This module translates the
 * control's `PropertyFilterProps.Query` into the backend's typed tree.
 *
 * WIRE (CR1, IVTHubModel commit 45883cd): the backend input
 * `sfdcPipelinePropertyFilter` is a **JSON string** on @httpQuery("propertyFilter")
 * (ListTaggedPipelines is a @paginated GET; a query string can't carry a nested
 * structure — same pattern nextToken uses). So `queryToPropertyFilter` returns the
 * logical object (or undefined), and the caller JSON.stringify's it into the input.
 * Splitting it this way keeps this function unit-testable on the object, not a string.
 *
 * See design 2026-07-29-pipeline-property-filter-design §4.1 / §5.4.
 */

import type { PropertyFilterProps } from '@amzn/awsui-components-react';

/** Backend field enum (SfdcPipelineFilterField). These MUST be the PropertyFilter
 *  `filteringProperties[].key` values so a token's propertyKey maps straight through.
 *  PIPELINE_SCORE (DAT-8462 Phase 2): the Engagement Depth Score is now a tree field
 *  too — values are numeric strings ("0".."3", multi-select in/notIn, not a range;
 *  backend rejects non-integer/negative only, no upper bound so the rubric can grow). */
export type SfdcPipelineFilterField =
  | 'SFDC_STAGE'
  | 'ACCOUNT_NAME'
  | 'OPP_NAME'
  | 'OPP_ID'
  | 'OPP_OWNER'
  | 'PIPELINE_SCORE'
  | 'IS_GREENFIELD'
  | 'CREATED_DATE'
  | 'CLOSE_DATE'
  | 'IS_BIG_BET'
  | 'HAS_IVT_ACTIVITY'
  | 'TAG_MATCHES_HIERARCHY';

const KNOWN_FIELDS: ReadonlySet<string> = new Set<SfdcPipelineFilterField>([
  'SFDC_STAGE',
  'ACCOUNT_NAME',
  'OPP_NAME',
  'OPP_ID',
  'OPP_OWNER',
  'PIPELINE_SCORE',
  'IS_GREENFIELD',
  'CREATED_DATE',
  'CLOSE_DATE',
  'IS_BIG_BET',
  'HAS_IVT_ACTIVITY',
  'TAG_MATCHES_HIERARCHY',
]);

/** Date fields (CREATED_DATE / CLOSE_DATE): set membership via = / != AND scalar
 *  range comparison via < <= > >= (backend rejects comparison ops on non-date
 *  fields with a 400 — we mirror by dropping them). Parity with the Impact filter
 *  (CR-299674749 gave Tagged the 4 fields Impact already had). */
const DATE_FIELDS: ReadonlySet<string> = new Set(['CREATED_DATE', 'CLOSE_DATE']);

/** Boolean fields that accept EXACTLY ONE of "true"/"false". Selecting both (or
 *  none) means "no constraint"; the backend's `ValueKind.BOOL.needsSingleValue()`
 *  is always true → a multi-value BOOL is a 400, so we drop the condition rather
 *  than emit values:["true","false"]. Applies to ALL bool flags here (verified in
 *  PropertyFilterEngine): IS_GREENFIELD (Greenfield = SFDC L3 "IND GFD" else
 *  Industry), IS_BIG_BET, HAS_IVT_ACTIVITY, TAG_MATCHES_HIERARCHY. */
const BOOL_SINGLE_VALUE_FIELDS: ReadonlySet<string> = new Set([
  'IS_GREENFIELD',
  'IS_BIG_BET',
  'HAS_IVT_ACTIVITY',
  'TAG_MATCHES_HIERARCHY',
]);

/** in / notIn: set membership (every field). lt/lte/gt/gte: scalar comparison on
 *  date fields only — the backend rejects them on other fields (400), and we mirror
 *  by dropping such a condition client-side. */
export type SfdcPipelineFilterOperator = 'in' | 'notIn' | 'lt' | 'lte' | 'gt' | 'gte';

export interface PropertyFilterCondition {
  field: SfdcPipelineFilterField;
  operator: SfdcPipelineFilterOperator;
  values: string[];
}

export interface PropertyFilterGroup {
  operation: 'and' | 'or';
  conditions: PropertyFilterCondition[];
}

export interface SfdcPipelinePropertyFilter {
  operation: 'and' | 'or';
  groups: PropertyFilterGroup[];
}

// A PropertyFilter token or (one level of) nesting. Mirrors the collection-hooks
// union; typed locally because the query we read may carry either shape under
// tokenGroups when enableTokenGroups is on.
type Token = PropertyFilterProps.Token;
type TokenOrGroup = PropertyFilterProps.Token | PropertyFilterProps.TokenGroup;

function isGroup(node: TokenOrGroup): node is PropertyFilterProps.TokenGroup {
  // A TokenGroup has `tokens`; a Token has `operator`. `operation` alone isn't
  // enough (both a group and the top-level query use it), but only a group nests
  // `tokens`.
  return Array.isArray((node as PropertyFilterProps.TokenGroup).tokens);
}

/** enum-token value → string[]. Cloudscape stores an enum token's value as an
 *  array (utils.js castValue), but be defensive: a scalar becomes a single-item
 *  array; null/undefined → []. Non-string entries are coerced then dropped if empty. */
function toValues(value: unknown): string[] {
  const raw = value == null ? [] : Array.isArray(value) ? value : [value];
  return raw
    .filter((v) => v != null)
    .map((v) => String(v))
    .filter((v) => v.length > 0);
}

/** Cloudscape operator → backend operator. `=`/`!=` → `in`/`notIn` on every field.
 *  Comparison operators (`<` `<=` `>` `>=`) only make sense on date fields — the
 *  backend rejects them elsewhere, so we drop any non-date condition that carries
 *  one. Any other operator → null (condition dropped). */
function toOperator(op: string | undefined, field: SfdcPipelineFilterField): SfdcPipelineFilterOperator | null {
  if (op === '=') return 'in';
  if (op === '!=') return 'notIn';
  if (!DATE_FIELDS.has(field)) return null;
  switch (op) {
    case '<':
      return 'lt';
    case '<=':
      return 'lte';
    case '>':
      return 'gt';
    case '>=':
      return 'gte';
    default:
      return null;
  }
}

/** One token → one condition, or null if it can't be represented (unknown field,
 *  unsupported operator, or empty values → drop). */
function tokenToCondition(t: Token): PropertyFilterCondition | null {
  const key = t.propertyKey;
  if (!key || !KNOWN_FIELDS.has(key)) return null; // unknown / free-text token → skip
  const field = key as SfdcPipelineFilterField;
  const operator = toOperator(t.operator, field);
  if (!operator) return null;
  const values = toValues(t.value);
  if (values.length === 0) return null; // empty selection → no constraint
  // BOOL single-value field (see BOOL_SINGLE_VALUE_FIELDS): backend
  // enforces exactly one of "true"/"false". Both selected (2 values) = no constraint
  // → drop, rather than send values:["true","false"] (→ 400). Exactly one passes.
  if (BOOL_SINGLE_VALUE_FIELDS.has(key) && values.length !== 1) return null;
  // Date comparison operators (lt/lte/gt/gte) take exactly one value; strip extras
  // so the request doesn't 400 (Cloudscape only emits one on a scalar-op token anyway).
  const isDateComparison =
    DATE_FIELDS.has(field) && (operator === 'lt' || operator === 'lte' || operator === 'gt' || operator === 'gte');
  const finalValues = isDateComparison && values.length > 1 ? [values[0]] : values;
  return { field, operator, values: finalValues };
}

/**
 * Translate a PropertyFilter query into the backend filter tree.
 * Returns `undefined` when the query carries no usable condition (so the caller
 * omits the param — the backend rejects an empty string via @pattern("^\\{")).
 *
 * Reads `tokenGroups` when present (enableTokenGroups=true); each top-level entry
 * that is a bare token becomes a single-condition group, and each nested group
 * becomes a multi-condition group. Falls back to flat `tokens` otherwise. The
 * backend supports exactly one level of nesting (Cloudscape can't emit deeper),
 * so we flatten any (unexpected) deeper group by lifting its tokens into the
 * current group rather than recursing.
 */
export function queryToPropertyFilter(
  query: PropertyFilterProps.Query,
): SfdcPipelinePropertyFilter | undefined {
  const topOperation: 'and' | 'or' = query.operation === 'or' ? 'or' : 'and';

  // Prefer tokenGroups (grouping mode); else treat flat tokens as one group per token.
  const nodes: readonly TokenOrGroup[] =
    query.tokenGroups && query.tokenGroups.length > 0 ? query.tokenGroups : query.tokens ?? [];

  const groups: PropertyFilterGroup[] = [];

  for (const node of nodes) {
    if (isGroup(node)) {
      const groupOp: 'and' | 'or' = node.operation === 'or' ? 'or' : 'and';
      const conditions: PropertyFilterCondition[] = [];
      // One level only: node.tokens should be plain tokens, but if a deeper group
      // slips in, lift its leaf tokens (best-effort flatten, never recurse).
      for (const child of node.tokens ?? []) {
        if (isGroup(child)) {
          for (const leaf of child.tokens ?? []) {
            if (!isGroup(leaf)) {
              const c = tokenToCondition(leaf);
              if (c) conditions.push(c);
            }
          }
        } else {
          const c = tokenToCondition(child);
          if (c) conditions.push(c);
        }
      }
      if (conditions.length > 0) groups.push({ operation: groupOp, conditions });
    } else {
      const c = tokenToCondition(node);
      // A bare top-level token = a single-condition group. Its group operation is
      // irrelevant (one condition) — use 'and' as the neutral default.
      if (c) groups.push({ operation: 'and', conditions: [c] });
    }
  }

  if (groups.length === 0) return undefined;
  return { operation: topOperation, groups };
}

/** Convenience for the caller: the JSON string the backend expects, or undefined. */
export function queryToPropertyFilterParam(query: PropertyFilterProps.Query): string | undefined {
  const tree = queryToPropertyFilter(query);
  return tree ? JSON.stringify(tree) : undefined;
}
